import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Sparkles, X, Trophy, Trash2, Brain, Crown, UserPlus } from "lucide-react";
import { API, useAuth } from "@/App";

export default function AdminContests() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contests, setContests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [authorized, setAuthorized] = useState(null); // null = checking
  const [pendingReports, setPendingReports] = useState(0);

  useEffect(() => {
    if (!user) return;
    fetch(`${API}/admin/me`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { is_admin: false })
      .then(d => {
        setAuthorized(d.is_admin === true);
        if (d.is_admin) {
          refresh();
          fetch(`${API}/admin/reports/stats`, { credentials: "include" })
            .then(r => r.ok ? r.json() : null)
            .then(s => s && setPendingReports((s.by_status?.pending || 0) + (s.by_status?.reviewing || 0)));
        } else setLoading(false);
      });
  }, [user]);

  const refresh = async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/contests`, { credentials: "include" });
    const data = await res.json();
    setContests(data.contests || []);
    setLoading(false);
  };

  const closeContest = async (id) => {
    if (!window.confirm("Close this contest, score entries, declare winners, and pay coin prizes?")) return;
    const res = await fetch(`${API}/admin/contests/${id}/close`, { method: "POST", credentials: "include" });
    const data = await res.json();
    if (!res.ok) {
      toast.error(typeof data.detail === "string" ? data.detail : "Failed");
      return;
    }
    toast.success(`Closed! ${data.winners?.length || 0} winners declared.`);
    refresh();
  };

  const cancelContest = async (id) => {
    if (!window.confirm("Cancel this contest? Entry fees will be refunded to entrants.")) return;
    const res = await fetch(`${API}/admin/contests/${id}`, { method: "DELETE", credentials: "include" });
    const data = await res.json();
    if (!res.ok) {
      toast.error(typeof data.detail === "string" ? data.detail : "Failed");
      return;
    }
    toast.success(`Cancelled. ${data.refunded_coins} coins refunded across ${data.entries} entries.`);
    refresh();
  };

  const runAiJudging = async (id) => {
    toast.info("Running AI judging — this may take ~30 seconds…");
    const res = await fetch(`${API}/admin/contests/${id}/judge-ai`, { method: "POST", credentials: "include" });
    const data = await res.json();
    if (!res.ok) {
      toast.error(typeof data.detail === "string" ? data.detail : "AI judging failed");
      return;
    }
    toast.success(`AI scored ${data.scored} of ${data.total} entries.`);
    refresh();
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  if (authorized === false) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
        <div className="bg-red-600 border-4 border-black p-8 rounded-lg max-w-md text-center shadow-[6px_6px_0_#fff]">
          <h1 className="text-3xl font-black mb-2">⛔ Admin Only</h1>
          <p className="mb-4">Your email isn't on the admin list.</p>
          <p className="text-xs text-pink-200">
            Add your email to <code>ADMIN_EMAILS</code> in <code>backend/.env</code> and restart backend.
          </p>
          <Button onClick={() => navigate("/contests")} className="mt-4 bg-white text-black border-2 border-black">
            Back to contests
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0014] to-[#000] text-white py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl font-black flex items-center gap-2">
            <Sparkles className="text-yellow-400" /> Contest Admin
          </h1>
          <div className="flex gap-2">
            <Button onClick={() => navigate("/contests")} variant="outline" className="border-2 border-white">
              View public page
            </Button>
            <Button onClick={() => navigate("/admin/reports")} variant="outline" className="border-2 border-pink-400 text-pink-300 relative" data-testid="nav-admin-reports">
              Report Inbox
              {pendingReports > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-black rounded-full min-w-[22px] h-[22px] flex items-center justify-center px-1 border-2 border-black animate-pulse" data-testid="report-badge-count">
                  {pendingReports > 99 ? "99+" : pendingReports}
                </span>
              )}
            </Button>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-yellow-400 text-black font-black border-2 border-black hover:bg-yellow-300"
              data-testid="admin-create-contest-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Contest
            </Button>
          </div>
        </div>

        {/* Founding Members panel */}
        <FoundingMembersPanel />

        <h2 className="text-2xl font-black mb-3 mt-10">Contests</h2>

        {loading ? (
          <p>Loading…</p>
        ) : contests.length === 0 ? (
          <div className="border-2 border-dashed border-pink-500/50 rounded-lg p-12 text-center">
            <Trophy className="w-12 h-12 text-pink-400 mx-auto mb-3" />
            <p className="text-pink-200 mb-3">No contests yet. Create your first one!</p>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-yellow-400 text-black font-black border-2 border-black"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Contest
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {contests.map(c => (
              <div key={c.contest_id} className="bg-white/5 border-2 border-pink-500/30 rounded-lg p-4 flex flex-wrap items-center gap-4" data-testid={`admin-contest-${c.contest_id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded uppercase ${
                      c.status === "active" ? "bg-red-500" :
                      c.status === "completed" ? "bg-green-600" : "bg-gray-600"
                    }`}>{c.status}</span>
                    <span className="text-[10px] bg-yellow-400 text-black px-2 py-0.5 font-black rounded uppercase">{c.category}</span>
                  </div>
                  <h3 className="font-black text-lg">{c.title}</h3>
                  <p className="text-xs text-pink-200">{c.theme}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    Prizes: 🥇{c.prize_first} · 🥈{c.prize_second} · 🥉{c.prize_third} · ends {new Date(c.end_at).toLocaleDateString()}
                  </p>
                </div>
                {c.status === "active" && (
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" onClick={() => runAiJudging(c.contest_id)} className="bg-purple-500 hover:bg-purple-400 border border-black">
                      <Brain className="w-3 h-3 mr-1" /> AI Score
                    </Button>
                    <Button size="sm" onClick={() => closeContest(c.contest_id)} className="bg-green-500 hover:bg-green-400 text-black font-bold border border-black" data-testid={`close-${c.contest_id}`}>
                      Close & Declare Winners
                    </Button>
                    <Button size="sm" onClick={() => cancelContest(c.contest_id)} variant="outline" className="border border-red-500 text-red-400 hover:bg-red-900">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                )}
                {c.status === "completed" && c.winners?.length > 0 && (
                  <div className="text-xs">
                    🏆 {c.winners.map(w => w.user_name).join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {showForm && <CreateContestForm onClose={() => setShowForm(false)} onCreated={refresh} />}
      </div>
    </div>
  );
}

function CreateContestForm({ onClose, onCreated }) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    theme: "",
    category: "general",
    end_at: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 16),
    entry_fee_coins: 0,
    prize_first: 1000,
    prize_second: 500,
    prize_third: 250,
    weight_likes: 0.4,
    weight_votes: 0.4,
    weight_ai: 0.2,
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      title: form.title,
      description: form.description,
      theme: form.theme,
      category: form.category,
      end_at: new Date(form.end_at).toISOString(),
      entry_fee_coins: parseInt(form.entry_fee_coins) || 0,
      prize_first: parseInt(form.prize_first) || 0,
      prize_second: parseInt(form.prize_second) || 0,
      prize_third: parseInt(form.prize_third) || 0,
      judging_weights: {
        likes: parseFloat(form.weight_likes),
        votes: parseFloat(form.weight_votes),
        ai: parseFloat(form.weight_ai),
      },
    };
    const res = await fetch(`${API}/admin/contests`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    setSubmitting(false);
    if (!res.ok) {
      toast.error(typeof data.detail === "string" ? data.detail : "Failed to create");
      return;
    }
    toast.success("Contest created and live!");
    onCreated();
    onClose();
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        className="bg-white text-black border-4 border-black rounded-lg shadow-[8px_8px_0_#FF1493] w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-black">New Weekly Contest</h2>
          <button type="button" onClick={onClose}><X /></button>
        </div>

        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={e => set("title", e.target.value)} required data-testid="form-title" placeholder='"Best 15-second cover song"' />
          </div>
          <div>
            <Label>Theme / prompt</Label>
            <Input value={form.theme} onChange={e => set("theme", e.target.value)} required data-testid="form-theme" placeholder="Cover any pop song from 2010-2015" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea rows={2} value={form.description} onChange={e => set("description", e.target.value)} required data-testid="form-description" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={e => set("category", e.target.value)} className="w-full border-2 border-black rounded h-10 px-2">
                <option value="general">General</option>
                <option value="music">Music</option>
                <option value="dance">Dance</option>
                <option value="comedy">Comedy</option>
                <option value="art">Art</option>
                <option value="fitness">Fitness</option>
              </select>
            </div>
            <div>
              <Label>Ends at</Label>
              <Input type="datetime-local" value={form.end_at} onChange={e => set("end_at", e.target.value)} required />
            </div>
          </div>
          <div className="grid grid-cols-4 gap-2">
            <div>
              <Label>1st 🪙</Label>
              <Input type="number" value={form.prize_first} onChange={e => set("prize_first", e.target.value)} />
            </div>
            <div>
              <Label>2nd 🪙</Label>
              <Input type="number" value={form.prize_second} onChange={e => set("prize_second", e.target.value)} />
            </div>
            <div>
              <Label>3rd 🪙</Label>
              <Input type="number" value={form.prize_third} onChange={e => set("prize_third", e.target.value)} />
            </div>
            <div>
              <Label>Entry fee 🪙</Label>
              <Input type="number" value={form.entry_fee_coins} onChange={e => set("entry_fee_coins", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Judging mix (must sum to 1.0)</Label>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <span className="text-xs">Likes</span>
                <Input type="number" step="0.1" min="0" max="1" value={form.weight_likes} onChange={e => set("weight_likes", e.target.value)} />
              </div>
              <div>
                <span className="text-xs">Votes</span>
                <Input type="number" step="0.1" min="0" max="1" value={form.weight_votes} onChange={e => set("weight_votes", e.target.value)} />
              </div>
              <div>
                <span className="text-xs">AI</span>
                <Input type="number" step="0.1" min="0" max="1" value={form.weight_ai} onChange={e => set("weight_ai", e.target.value)} />
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-yellow-400 text-black font-black border-2 border-black hover:bg-yellow-300"
            data-testid="form-submit"
          >
            {submitting ? "Creating…" : "Launch Contest"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function FoundingMembersPanel() {
  const [founders, setFounders] = useState([]);
  const [emails, setEmails] = useState("");
  const [note, setNote] = useState("");
  const [granting, setGranting] = useState(false);
  const [showInput, setShowInput] = useState(false);

  const refresh = async () => {
    const r = await fetch(`${API}/admin/founding/list`, { credentials: "include" });
    if (r.ok) {
      const d = await r.json();
      setFounders(d.founders || []);
    }
  };

  useEffect(() => { refresh(); }, []);

  const grant = async () => {
    const list = emails.split(/[\s,;\n]+/).map(e => e.trim()).filter(Boolean);
    if (list.length === 0) {
      toast.error("Paste at least one email");
      return;
    }
    setGranting(true);
    try {
      const res = await fetch(`${API}/admin/founding/grant`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: list, note: note || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed");
      const parts = [];
      if (data.granted?.length) parts.push(`${data.granted.length} granted`);
      if (data.already_founders?.length) parts.push(`${data.already_founders.length} already`);
      if (data.not_found?.length) parts.push(`${data.not_found.length} not found`);
      toast.success(parts.join(" · "));
      if (data.not_found?.length) {
        toast.error(`Not found: ${data.not_found.join(", ")}`);
      }
      setEmails("");
      setNote("");
      setShowInput(false);
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setGranting(false);
    }
  };

  const revoke = async (email) => {
    if (!window.confirm(`Revoke founding member from ${email}?`)) return;
    const res = await fetch(`${API}/admin/founding/revoke`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      const d = await res.json();
      toast.error(d.detail || "Failed");
      return;
    }
    toast.success(`Revoked ${email}`);
    refresh();
  };

  return (
    <div className="bg-gradient-to-br from-yellow-500/10 to-amber-500/10 border-2 border-yellow-500/40 rounded-lg p-5 mb-8" data-testid="founding-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Crown className="text-yellow-400" />
          <h2 className="text-2xl font-black">Founding Members</h2>
          <span className="bg-yellow-400 text-black text-xs font-black px-2 py-0.5 rounded">{founders.length}</span>
        </div>
        <Button
          onClick={() => setShowInput(v => !v)}
          className="bg-yellow-400 text-black font-black border-2 border-black hover:bg-yellow-300"
          data-testid="founding-grant-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" /> {showInput ? "Cancel" : "Grant Lifetime Gold"}
        </Button>
      </div>
      <p className="text-sm text-yellow-200/80 mb-3">
        Hand-pick early adopters and gift them lifetime OurSpacez Gold (no Stripe charge). Great for soft-launch social proof.
      </p>

      {showInput && (
        <div className="space-y-2 mb-4 bg-black/40 border border-yellow-500/30 rounded p-3">
          <Label className="text-yellow-200">Emails (one per line, or comma-separated)</Label>
          <Textarea
            rows={4}
            placeholder="alice@example.com&#10;bob@example.com"
            value={emails}
            onChange={e => setEmails(e.target.value)}
            className="bg-black border-yellow-500/40 text-white font-mono text-sm"
            data-testid="founding-emails-input"
          />
          <Label className="text-yellow-200">Note (optional)</Label>
          <Input
            placeholder="Beta launch invite"
            value={note}
            onChange={e => setNote(e.target.value)}
            className="bg-black border-yellow-500/40 text-white"
          />
          <Button
            onClick={grant}
            disabled={granting}
            className="bg-yellow-400 text-black font-black border-2 border-black hover:bg-yellow-300"
            data-testid="founding-confirm-btn"
          >
            {granting ? "Granting…" : "Grant Lifetime Gold"}
          </Button>
        </div>
      )}

      {founders.length === 0 ? (
        <p className="text-sm text-yellow-200/60 italic" data-testid="founding-empty">
          No founding members yet. Grant some to seed your launch.
        </p>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {founders.map(f => (
            <div key={f.user_id} className="flex items-center gap-3 bg-black/30 border border-yellow-500/20 rounded p-2 text-sm" data-testid={`founder-${f.user_id}`}>
              <Crown className="w-4 h-4 text-yellow-400 flex-shrink-0" />
              {f.picture ? (
                <img src={f.picture} alt="" className="w-7 h-7 rounded-full" />
              ) : (
                <div className="w-7 h-7 rounded-full bg-yellow-500/30" />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{f.name || "Unnamed"}</p>
                <p className="text-xs text-yellow-200/70 truncate">{f.email}</p>
              </div>
              <span className="text-[10px] text-yellow-200/50">
                {f.founder_granted_at ? new Date(f.founder_granted_at).toLocaleDateString() : ""}
              </span>
              <button
                onClick={() => revoke(f.email)}
                className="text-red-400 hover:text-red-200 text-xs ml-2"
                data-testid={`revoke-${f.user_id}`}
              >
                Revoke
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

